import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import ThemeToggle from './ThemeToggle'
import { isStaff } from '../utils/roles'
import { useAuthLink } from '../hooks/useAuthModal'

type Props = {
	user: { role?: string; avatar_url?: string | null } | null
	theme: 'light' | 'dark'
	/** Зарезервирован вызывающими (историческое API), внутри не нужен. */
	onAccountClick?: () => void
	onToggleNotifications?: () => void
	notificationsOpen?: boolean
	/** Есть непрочитанные: точка на аватаре и колокольчике (на телефоне колокольчик — только здесь). */
	hasUnreadNotifications?: boolean
	onCloseSidebar?: () => void
	onLogout?: () => void
	isSidebarOpen?: boolean
}

// Tab 'overview' теперь label «Профиль» (см. i18n) — он включает identity-
// блок. Отдельный tab 'profile' удалён, AccountProfile.tsx не используется.
//
// Tab 'generations' скрыт (consistent с AccountSidebar): файлы доступны через
// главный sidebar «Мои генерации» (/files route).
const NAV_ITEMS: { key: string; labelKey: string; icon: string; color: string; staffOnly?: boolean }[] = [
	{
		key: 'overview',
		labelKey: 'account.overview',
		icon: '/icons/user_lk.svg',
		color: 'rgba(142, 100, 220, 0.15)',
	},
	{
		key: 'payments',
		labelKey: 'account.payments',
		icon: '/icons/card_lk.svg',
		color: 'rgba(52, 168, 130, 0.15)',
	},
	{
		key: 'referrals',
		labelKey: 'account.referrals',
		icon: '/icons/ref_lk.svg',
		color: 'rgba(52, 168, 168, 0.15)',
	},
	// Персонажи — зеркало таба кабинета (AccountSidebar): мобильное меню не
	// должно терять разделы (скрин владельца). Тот же tab-канал handleNavClick.
	{
		key: 'characters',
		labelKey: 'account.characters',
		icon: '/icons/user_dark.svg',
		color: 'rgba(139, 92, 246, 0.15)',
	},
]

const ChevronIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
)

// onAccountClick/onLogout из Props намеренно не деструктурируются: пункт
// «Выйти» закомментирован ниже (выход живёт в кабинете), а onAccountClick —
// историческое API вызывающих.
const SidebarQuickMenu = ({
	user,
	theme,
	onToggleNotifications,
	notificationsOpen = false,
	hasUnreadNotifications = false,
	onCloseSidebar,
	isSidebarOpen = false,
}: Props) => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [activeTab, setActiveTab] = useState<string>(
		() => sessionStorage.getItem('_current_account_tab') || 'overview',
	)
	const rootRef = useRef<HTMLDivElement | null>(null)
	const navigate = useNavigate()
	const location = useLocation()
	// «Войти» — окном поверх страницы; меню закрывается, чтобы не остаться открытым под окном и после входа
	const authLink = useAuthLink()

	const isOnAccount = location.pathname === '/account'
	// Колокольчик лежит внутри этого меню: точка на аватаре ведёт к нему, пока меню закрыто, — дальше её несёт сам колокольчик.
	const unread = !!user && hasUnreadNotifications

	const close = () => setOpen(false)
	const toggle = () => setOpen((v) => !v)

	// Подстройка состояния на смену пропов/роута — ВО ВРЕМЯ рендера
	// (официальный паттерн «adjust state when props change»), не в эффектах:
	// react-hooks/set-state-in-effect. Семантика прежняя: закрытие сайдбара
	// закрывает поповер; уход с /account гасит подсветку таба; возврат на
	// /account и открытие панели восстанавливают её из sessionStorage.
	const [prevAdjust, setPrevAdjust] = useState({ sidebar: isSidebarOpen, onAccount: isOnAccount, open })
	if (prevAdjust.sidebar !== isSidebarOpen || prevAdjust.onAccount !== isOnAccount || prevAdjust.open !== open) {
		if (prevAdjust.sidebar !== isSidebarOpen && !isSidebarOpen) setOpen(false)
		if (prevAdjust.onAccount !== isOnAccount) {
			if (!isOnAccount) setActiveTab('')
			else {
				const saved = sessionStorage.getItem('_current_account_tab')
				if (saved) setActiveTab(saved)
			}
		}
		if (prevAdjust.open !== open && open && isOnAccount) {
			const saved = sessionStorage.getItem('_current_account_tab')
			if (saved) setActiveTab(saved)
		}
		setPrevAdjust({ sidebar: isSidebarOpen, onAccount: isOnAccount, open })
	}

	// Гость: сайдбар открылся → панель раскрывается сама (350мс — после
	// анимации сайдбара). setTimeout-колбэк — законный setState вне тела.
	useEffect(() => {
		if (!user && isSidebarOpen) {
			const timer = setTimeout(() => setOpen(true), 350)
			return () => clearTimeout(timer)
		}
	}, [isSidebarOpen, user])

	useEffect(() => {
		const handler = (e: Event) => {
			const tab = (e as CustomEvent<string>).detail
			if (tab) {
				setActiveTab(tab)
				sessionStorage.setItem('_current_account_tab', tab)
			}
		}
		window.addEventListener('account:set-tab', handler)
		return () => window.removeEventListener('account:set-tab', handler)
	}, [])

	const handleNavClick = (tab: string) => {
		setActiveTab(tab)
		sessionStorage.setItem('_current_account_tab', tab)
		close()
		onCloseSidebar?.()
		if (isOnAccount) {
			window.dispatchEvent(new CustomEvent('account:set-tab', { detail: tab }))
		} else {
			sessionStorage.setItem('account_tab', tab)
			navigate('/account')
		}
	}

	// handleLogoutClick удалён вместе с деструктуризацией onLogout: пункт
	// «Выйти» в панели закомментирован (см. ниже) — выход живёт в кабинете.

	useEffect(() => {
		if (!open) return

		const onDocPointerDown = (e: PointerEvent) => {
			const target = e.target as Node
			if (rootRef.current && !rootRef.current.contains(target)) close()
		}

		const onEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape') close()
		}

		document.addEventListener('pointerdown', onDocPointerDown)
		document.addEventListener('keydown', onEsc)
		return () => {
			document.removeEventListener('pointerdown', onDocPointerDown)
			document.removeEventListener('keydown', onEsc)
		}
	}, [open])

	return (
		<div className={`sidebar-quick ${open ? 'is-open' : ''}`} ref={rootRef}>
			<button
				type="button"
				className={`sidebar-quick__trigger ${open ? 'is-open' : ''}`}
				onClick={toggle}
				aria-expanded={open}
				aria-label={open ? t('nav.closeMenu') : unread ? t('nav.openMenuUnread') : t('nav.openMenu')}
			>
				{open ? (
					<span className="sidebar-quick__sliders" aria-hidden="true">
						<span className="s s1" />
						<span className="s s2" />
					</span>
				) : user?.avatar_url ? (
					<img src={user.avatar_url} alt="" className="sidebar-quick__avatar" />
				) : (
					<svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M10.9102 15.4287C15.302 15.4287 19.2257 17.4291 21.8203 20.5713C19.2257 23.7135 15.3021 25.7139 10.9102 25.7139C6.51823 25.7139 2.59457 23.7135 0 20.5713C2.59457 17.4291 6.51827 15.4287 10.9102 15.4287ZM10.9102 0C12.6151 0 14.2505 0.677221 15.4561 1.88281C16.6616 3.08839 17.3389 4.72379 17.3389 6.42871C17.3388 8.1336 16.6616 9.76907 15.4561 10.9746C14.2505 12.1801 12.6151 12.8574 10.9102 12.8574C9.20524 12.8574 7.56984 12.1801 6.36426 10.9746C5.15871 9.76907 4.4815 8.1336 4.48145 6.42871C4.48145 4.72379 5.15873 3.08839 6.36426 1.88281C7.56985 0.677221 9.20519 0 10.9102 0Z" fill="#8E97AD"/>
					</svg>
				)}
				{unread && !open && <span className="notification-dot" aria-hidden="true" />}
			</button>

			<div id="sidebar-quick-panel" className={`sidebar-quick__panel ${open ? 'is-open' : ''}`}>

				{user && (
					<>
						<nav className="sqm-nav">
							{NAV_ITEMS.filter((item) => !item.staffOnly || isStaff(user?.role)).map((item) => (
								<button
									key={item.key}
									type="button"
									className={`sqm-nav__item ${isOnAccount && activeTab === item.key ? 'sqm-nav__item--active' : ''}`}
									onClick={() => handleNavClick(item.key)}
								>
									<span className="sqm-nav__icon-wrap" style={{ background: item.color }}>
										<img src={item.icon} alt="" width="18" height="18" />
									</span>
									<span className="sqm-nav__label">{t(item.labelKey)}</span>
									<span className="sqm-nav__chevron">
										<ChevronIcon />
									</span>
								</button>
							))}

{/* <div className="sqm-divider sqm-divider--sm" />

							<button
								type="button"
								className="sqm-nav__item sqm-nav__item--logout"
								onClick={handleLogoutClick}
							>
								<span className="sqm-nav__icon-wrap sqm-nav__icon-wrap--logout">
									<img src="/icons/exit_lk.svg" alt="" width="18" height="18" />
								</span>
								<span className="sqm-nav__label">Выйти</span>
								<span className="sqm-nav__chevron">
									<ChevronIcon />
								</span>
							</button> */}
						</nav>

						<div className="sqm-divider" />
					</>
				)}

				<div className="sidebar-theme-toggle">
					{user ? (
						<>
							<a
								href="https://telegram.me/partnershipsads"
								target="_blank"
								rel="noopener noreferrer"
								className="sqm-header__icon-btn"
								title={t('nav.support')}
							>
								<img src="/icons/consultant.svg" alt="" width="18" height="18" />
							</a>
							<button
								type="button"
								className={`sqm-header__icon-btn${notificationsOpen ? ' sqm-header__icon-btn--active' : ''}`}
								onPointerDown={(e) => e.stopPropagation()}
								onClick={(e) => {
									e.stopPropagation()
									onToggleNotifications?.()
								}}
								title={t('header.notifications')}
								aria-label={unread && !notificationsOpen ? t('header.notificationsUnread') : undefined}
							>
								{notificationsOpen ? (
									<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
										<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
									</svg>
								) : (
									<img
										src={theme === 'dark' ? '/icons/noty_dark.svg' : '/icons/noty_light.svg'}
										alt=""
										width="18"
										height="18"
									/>
								)}
								{unread && !notificationsOpen && <span className="notification-dot" aria-hidden="true" />}
							</button>
						</>
					) : (
						<>
							<a {...authLink('/login', { onOpen: onCloseSidebar })} className="sqm-login-btn" title={t('header.login')}>
								<img src="/icons/user_dark.svg" alt="" width="14" height="14" />
								{t('header.login')}
							</a>
							<a
								href="https://telegram.me/partnershipsads"
								target="_blank"
								rel="noopener noreferrer"
								className="sqm-header__icon-btn"
								title={t('nav.support')}
							>
								<img src="/icons/consultant.svg" alt="" width="18" height="18" />
							</a>
						</>
					)}
					<ThemeToggle className="sidebar-theme" />
				</div>
			</div>
		</div>
	)
}

export default SidebarQuickMenu
